/**
 * SSO Authentication Wrapper Component
 * Protects routes and handles SSO authentication
 */

import React, { useEffect, useState } from 'react';
import { ssoService, SSOUser } from './ssoService';

interface SSOAuthProps {
  children: React.ReactNode;
  loadingComponent?: React.ReactNode;
  unauthorizedComponent?: React.ReactNode;
}

export const SSOAuth: React.FC<SSOAuthProps> = ({ 
  children, 
  loadingComponent,
  unauthorizedComponent 
}) => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);
  const [user, setUser] = useState<SSOUser | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const checkAuth = async () => {
      try {
        console.log('[SSOAuth Accounts] ===== STARTING AUTH CHECK =====');
        console.log('[SSOAuth Accounts] Current URL:', window.location.href);
        console.log('[SSOAuth Accounts] Hostname:', window.location.hostname);
        console.log('[SSOAuth Accounts] Port:', window.location.port);
        
        // Run diagnostic
        (ssoService as any).diagnose();
        
        // Check if logout was triggered from another tab/app
        // BUT only if we don't have a valid token in URL/localStorage
        const urlParams = new URLSearchParams(window.location.search);
        const urlToken = urlParams.get('sso_token');
        const hasTokenInStorage = localStorage.getItem('sso_token') || localStorage.getItem('auth_token');
        
        // Only check logout flag if we don't have a token (to avoid false positives)
        if (!urlToken && !hasTokenInStorage && ssoService.checkLogoutFlag()) {
          console.log('[SSOAuth Accounts] Logout detected from another app (no token found), redirecting...');
          console.log('[SSOAuth Accounts] ===== LOGOUT FLAG BREAKPOINT =====');
          debugger;
          setIsAuthenticated(false);
          setLoading(false);
          ssoService.redirectToSSO();
          return;
        }
        
        // If we have a token, clear logout flag (it's a false alarm)
        if ((urlToken || hasTokenInStorage) && localStorage.getItem('sso_logout_flag') === 'true') {
          console.log('[SSOAuth Accounts] Logout flag found but token exists - clearing flag and continuing');
          localStorage.removeItem('sso_logout_flag');
        }

        console.log('[SSOAuth Accounts] Calling isAuthenticated()...');
        const authenticated = await ssoService.isAuthenticated();
        console.log('[SSOAuth Accounts] Authentication result:', authenticated);
        setIsAuthenticated(authenticated);
        
        if (authenticated) {
          console.log('[SSOAuth Accounts] User is authenticated, fetching user info...');
          const currentUser = await ssoService.getCurrentUser();
          console.log('[SSOAuth Accounts] User info:', currentUser);
          setUser(currentUser);
        } else {
          console.log('[SSOAuth Accounts] User is NOT authenticated, redirecting to SSO portal...');
          console.log('[SSOAuth Accounts] ===== AUTHENTICATION FAILED BREAKPOINT =====');
          console.log('[SSOAuth Accounts] Authentication check returned false');
          console.log('[SSOAuth Accounts] ============================================');
          
          // DEBUG: Breakpoint before redirect
          debugger;
          
          // Not authenticated, redirect to SSO portal
          ssoService.redirectToSSO();
        }
      } catch (error) {
        console.error('[SSOAuth Accounts] Auth check failed:', error);
        const errorMessage = error instanceof Error ? error.message : String(error);
        console.error('[SSOAuth Accounts] Error details:', errorMessage);
        console.error('[SSOAuth Accounts] ===== ERROR BREAKPOINT =====');
        console.error('[SSOAuth Accounts] Exception occurred during auth check');
        console.error('[SSOAuth Accounts] ===========================');
        
        // DEBUG: Breakpoint before redirect
        debugger;
        
        setIsAuthenticated(false);
        ssoService.redirectToSSO();
      } finally {
        setLoading(false);
      }
    };

    checkAuth();

    // Listen for storage events (logout from other tabs/apps) - Google-style
    const handleStorageChange = (e: StorageEvent) => {
      // Check for logout flag
      if (e.key === 'sso_logout_flag' && e.newValue === 'true') {
        console.log('[SSOAuth Accounts] Storage event: Logout flag detected from another tab/app');
        // Verify session is actually invalidated before redirecting
        (ssoService as any).checkSessionStatus?.().then((isValid: boolean) => {
          if (!isValid) {
            console.log('[SSOAuth Accounts] Storage event: Session confirmed invalidated, redirecting...');
            setIsAuthenticated(false);
            setUser(null);
            ssoService.clearToken();
            ssoService.redirectToSSO();
          } else {
            console.log('[SSOAuth Accounts] Storage event: Logout flag found but session still valid - clearing flag');
            localStorage.removeItem('sso_logout_flag');
          }
        }).catch(() => {
          // If check fails, assume logout is valid
          setIsAuthenticated(false);
          setUser(null);
          ssoService.clearToken();
          ssoService.redirectToSSO();
        });
        return;
      }
      
      // Check for logout event broadcast
      if (e.key === 'sso_logout_event' && e.newValue) {
        console.log('[SSOAuth Accounts] Storage event: Logout event detected from another tab/app');
        setIsAuthenticated(false);
        setUser(null);
        ssoService.clearToken();
        ssoService.redirectToSSO();
        return;
      }
      
      // Also check if token was removed
      if ((e.key === 'sso_token' || e.key === 'auth_token') && !e.newValue && e.oldValue) {
        console.log('[SSOAuth Accounts] Storage event: Token removed from another tab/app');
        setIsAuthenticated(false);
        setUser(null);
        ssoService.redirectToSSO();
      }
    };

    window.addEventListener('storage', handleStorageChange);

    // Periodic session validation check (every 30 seconds) - Google-style
    // This ensures cross-app logout is detected quickly
    const validationInterval = setInterval(async () => {
      if (isAuthenticated) {
        try {
          // Use lightweight session check if available, otherwise full validation
          const sessionValid = await (ssoService as any).checkSessionStatus?.() || await ssoService.isAuthenticated();
          if (!sessionValid) {
            console.log('[SSOAuth Accounts] Periodic check: Session invalidated (cross-app logout detected), redirecting...');
            setIsAuthenticated(false);
            setUser(null);
            ssoService.clearToken();
            ssoService.redirectToSSO();
          }
        } catch (error) {
          console.error('[SSOAuth Accounts] Periodic validation check failed:', error);
          // On error, do full validation as fallback
          try {
            const authenticated = await ssoService.isAuthenticated();
            if (!authenticated) {
              console.log('[SSOAuth Accounts] Periodic check (fallback): Token invalidated, redirecting...');
              setIsAuthenticated(false);
              setUser(null);
              ssoService.redirectToSSO();
            }
          } catch (fallbackError) {
            console.error('[SSOAuth Accounts] Periodic validation fallback failed:', fallbackError);
          }
        }
      }
    }, 30000); // Check every 30 seconds

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      clearInterval(validationInterval);
    };
  }, [isAuthenticated]);

  if (loading) {
    if (loadingComponent) {
      return <>{loadingComponent}</>;
    }
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        flexDirection: 'column',
        gap: '1rem'
      }}>
        <div className="spinner" style={{
          border: '4px solid #f3f3f3',
          borderTop: '4px solid #3498db',
          borderRadius: '50%',
          width: '40px',
          height: '40px',
          animation: 'spin 1s linear infinite'
        }}></div>
        <p>Validating SSO session...</p>
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  if (!isAuthenticated) {
    if (unauthorizedComponent) {
      return <>{unauthorizedComponent}</>;
    }
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        flexDirection: 'column',
        gap: '1rem'
      }}>
        <p>Redirecting to login...</p>
      </div>
    );
  }

  return <>{children}</>;
};

export default SSOAuth;
